import { push } from "connected-react-router";
import JSZip from "jszip";
import { call, delay, put } from "redux-saga/effects";

import {
  addCommitError,
  getIdeProjectStarterCodeError,
  getMyRepositoryError,
  getProjectByRepositoryError,
  getRepositoryError,
  getTextJamTemplateError,
  loadProjectByCommitError,
  receivedIdeProjectStarterCode,
  receivedMyRepository,
  receivedProjectByCommit,
  receivedProjectByRepository,
  receivedRepository,
  receivedResponseOnCommit,
  receivedTextJamTemplate,
} from "../actions/ide";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../actions/notification";
import { getScratchStarterCode } from "../api/graphql/query/scracthStarterCode.queries";

import * as ideApi from '../api/ide';
import { TIMEOUT_RESPONSE_ERROR_CODE } from "../components/IDE/TextJam/TextJamEditor/constants";

const successAddedCommit = "All set! Your project has been saved.";
const errorAddedCommit = "Oops! We couldn't save your project. Please try again or check your connection.";
const noNewChangesCommit = "No new code changes to save!";
const successUpdatedProjectName = "Project name updated! No new code changes to save.";

const errorLoadedProject = "Uh-oh! Something went wrong—try again.";

const getErrorTimeoutMessage = (action) => {
  return `We couldn’t ${action} your project. Please wait a moment and try again.`;
}

export function* createTextJamCommit({ payload }) {
  try {
    const hasContentChanges = payload.hasContentChanges === true;
    const commitArgs = {
      name: payload.project.name, 
      project_type: payload.project.project_type, 
      repositoryId: payload.repositoryId, 
      commitId: payload.commitId,
      extension: payload.project.extension,
      hasContentChanges,
      programId: payload.programId,
      activitySetId: payload.activitySetId,
    };
    const commitData = yield call(ideApi.createTextJamCommit, commitArgs);

    const {
      uploadUrl,
      ticketId,
      repository,
      commit,
      hasContentChanges: responseHasContentChanges,
    } = commitData.data;

    if (!repository) {
      yield put(showErrorMessage(errorAddedCommit));
      yield put(addCommitError());
      return;
    }

    if (responseHasContentChanges === false || !hasContentChanges) {
      yield put(receivedResponseOnCommit({ repository, commit }));

      yield put(
        showSuccessMessage(
          payload.hasNameChanges
            ? successUpdatedProjectName
            : noNewChangesCommit,
        ),
      );

      return;
    }

    if (!uploadUrl || !ticketId || !commit) {
      yield put(showErrorMessage(errorAddedCommit));
      yield put(addCommitError());
      return;
    }

    const pushData = yield call(ideApi.uploadTextJamFiles, { uploadUrl, ticketId, commitBlob: payload.commitBlob });
    const { commitId } = pushData.data;

    if (!commitId) {
      yield put(showErrorMessage(errorAddedCommit));
      yield put(addCommitError());
      return;
    }

    yield put(receivedResponseOnCommit({ repository, commit, currentProject: payload.commitBlob }));
    yield put(showSuccessMessage(successAddedCommit));
  } catch (e) {
    const response = e?.response?.data;
    const errorMessage = response?.message ?? errorAddedCommit;
    if (e.code === TIMEOUT_RESPONSE_ERROR_CODE) {
      yield put(showErrorMessage(getErrorTimeoutMessage("save")));
    } else {
      yield put(showErrorMessage(errorMessage));
    }
    
    yield put(addCommitError());
    console.log(e);
  }
}

export function* addCommit({ payload }) {
  try {
    const { data } = yield call(ideApi.addCommit, payload);
    const { repository, commit } = data;

    if (!repository?.id || !commit?.id) {
      yield put(showErrorMessage(errorAddedCommit));
      yield put(addCommitError());
      return;
    }

    const maxAttempts = 15;
    const interval = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const { data: isCommitSaved } = yield call(
        ideApi.getIsCommitSaved,
        repository.id,
        commit.id
      );

      if (!!isCommitSaved) {
        yield put(receivedResponseOnCommit({ repository, commit }));
        yield put(showSuccessMessage(successAddedCommit));
        return;
      }
      yield delay(interval);
    }

    yield put(showErrorMessage(errorAddedCommit));
    yield put(addCommitError());
  } catch (e) {
    yield put(showErrorMessage(errorAddedCommit));
    yield put(addCommitError());
    console.log(e);
  }
}

export function* getProjectByCommit({ payload }) {
  try {
    const { repositoryId, commitId } = payload;

    const { data } = yield call(ideApi.getProjectByCommit, repositoryId, commitId);

    if (!data) {
      yield put(showErrorMessage(errorLoadedProject));
      yield put(loadProjectByCommitError());
    } else {
      yield put(receivedProjectByCommit({ commitId, project: data }));
    }

  } catch (e) {
    e.code === TIMEOUT_RESPONSE_ERROR_CODE
      ? yield put(showErrorMessage(getErrorTimeoutMessage("load")))
      : yield put(showErrorMessage(errorLoadedProject));

    yield put(loadProjectByCommitError());
    console.log(e);
  }
}

export function* getMyRepository({ payload }) {
  try {
    const { data } = yield call(ideApi.getMyRepository, payload);

    if (data?.error) {
      yield put(getMyRepositoryError());
      yield put(push('/home', { skipNavigationGuard: true }));
    } else {
      yield put(receivedMyRepository({ repository: data }));
    }

  } catch (e) {
    yield put(getMyRepositoryError());
    yield put(push('/home', { skipNavigationGuard: true }));
    console.log(e);
  }
}

export function* getProjectByRepository({ payload }) {
  try {
    const { data } = yield call(ideApi.getProjectByRepository, payload);

    if (!data) {
      yield put(showErrorMessage(errorLoadedProject));
      yield put(getProjectByRepositoryError());
    } else {
      yield put(receivedProjectByRepository({ project: data }));
    }

  } catch (e) {
    e.code === TIMEOUT_RESPONSE_ERROR_CODE
      ? yield put(showErrorMessage(getErrorTimeoutMessage("load")))
      : yield put(showErrorMessage(e.message || errorLoadedProject));

    yield put(getProjectByRepositoryError());
    console.log(e);
  }
}

export function* getRepository({ payload }) {
  try {
    const { data } = yield call(ideApi.getRepository, payload);

    if (data?.error) {
      yield put(getRepositoryError());
    } else {
      yield put(receivedRepository({ repository: data }));
    }

  } catch (e) {
    yield put(getRepositoryError());
    console.log(e);
  }
}

export function* getTextJamTemplate({ payload }) {
  try {
    const {data} = yield call(ideApi.getTextJamTemplate, payload);

    const zip = yield call(JSZip.loadAsync, data);

    const file = zip.file("project.json");

    if (!file) {
      throw new Error("Incorrect template");
    }

    const content = JSON.parse(yield call([file, file.async], "string"));

    if (data?.error) {
      throw new Error("Incorrect template");
    } else {
      yield put(receivedTextJamTemplate({ template: content }));
    }

  } catch (e) {
    yield put(getTextJamTemplateError());
    console.log(e);
    yield put(showErrorMessage(errorLoadedProject));
  }
}

export function* getIdeProjectStarterCode({ payload }) {
  try {
    const { id } = payload || {};

    if (!id) {
      yield put(showErrorMessage(errorLoadedProject));
      yield put(getIdeProjectStarterCodeError());
      return;
    }

    const res = yield call(getScratchStarterCode, id);
    const base64Str = res?.data?.ScratchStarterCodeQuery?.base64Str;

    if (!base64Str) {
      yield put(showErrorMessage(errorLoadedProject));
      yield put(getIdeProjectStarterCodeError());
      return;
    }

    const binaryString = atob(base64Str);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const content = new Blob([bytes.buffer], { type: "application/zip" });

    yield put(receivedIdeProjectStarterCode({ content }));
  } catch (e) {
    console.error(e);
    yield put(showErrorMessage(errorLoadedProject));
    yield put(getIdeProjectStarterCodeError());
  }
}
